import {
  Box,
  Button,
  Container,
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from "@material-ui/core";
import React from "react";
import { colors } from "swyft-react-core";
import { ReviewContainer } from "../quotePage/ReviewContainer";
import { Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { EQuoteType } from "../../api/Types";
import { RootState } from "../../app/rootReducer";
import { EEventType, eventAnalytic } from "../../app/analytics";
import OverrideTheme from "../../components/OverrideTheme";
import { DepositAmountToken } from "../../app/consts";
import { quoteDepositAmount } from "../../app/utils";

const useStyles = makeStyles((theme) => ({
  root: {},
  alternate: {
    background: colors.alternate
  },
  button: {
    width: "90%",
    maxWidth: "17.5rem"
  },
  banner: {
    "backgroundSize": "cover",
    "backgroundPosition": "center",
    "width": "100%",
    "display": "flex",
    "alignItems": "center",
    "height": "120px",
    "backgroundImage": "url(images/sm/img-header-payment.jpg)",
    [theme.breakpoints.up("sm")]: {
      height: "210px"
    },
    [theme.breakpoints.up("md")]: {
      backgroundImage: "url(images/md/img-header-payment.jpg)",
      height: "360px"
    },
    [theme.breakpoints.up("lg")]: {
      backgroundImage: "url(images/lg/img-header-payment.jpg)"
    },
    [theme.breakpoints.up("xl")]: {
      backgroundImage: "url(images/xl/img-header-payment.jpg)"
    },
    "& h5": {
      maxWidth: "50%"
    }
  },
  images: {
    display: "flex",
    height: 400,
    margin: "auto"
  },
  table: {
    "& td": {
      borderBottom: 0,
      padding: "4px"
    }
  },
  alignRight: {
    textAlign: "right"
  }
}));

const strings = {
  "payment.header": "Payment Details",

  "payment.section.1.header": "Payment Details & Terms",
  "payment.section.1.p.1":
    "Here are our payment details and our full terms and conditions. Please read before purchase.",

  "payment.section.2.header": "Payment",
  "payment.section.2.p.1.boiler": "Full payment is required 2 days prior to installation.",
  "payment.section.2.p.2.boiler":
    "We offer a 12 month workmanship guarantee on parts installed by Swyft Energy. This does not cover pipework or parts of your heating system not installed by Swyft Energy.",
  "payment.section.2.p.3.boiler":
    "If a job is cancelled/postponed 24hrs prior to installation, a 10% fee will be charged.",
  "payment.section.2.p.1.solar":
    "Deposit of €{deposit_amount} is required to lock in your price and book a place in the installation queue. Second and final payment of 100% of the total cost, less €{deposit_amount} deposit paid, is due a minimum of one week prior to installation in order to release materials and labour.",
  "payment.section.2.p.2.solar": "We offer 24-month workmanship & happiness guarantee with every install.",
  "payment.section.2.p.3.solar":
    "If job is cancelled/postponed 48hrs prior to installation a fee of 10% will be charged.",

  "payment.section.3.header": "Payment details",
  "payment.section.3.p.1":
    "Please note that you shall be deemed to have accepted our terms and conditions upon payment of the deposit referred to in your invoice.",

  "payment.section.4.header": "Assumptions",
  "payment.section.4.p.1.boiler": "Sufficient gas & water pressure in system with no existing leaks.",
  "payment.section.4.p.1.solar":
    "Swyft Energy only accept responsibility for areas of the roof structure that were specifically worked upon as part of roof mounted Solar PV system. The scope of works does not apply to existing reworks required to other parts of the roof structure.",
  "payment.section.4.p.2.solar":
    "Swyft Energy only accept responsibility for the works associated with the DC & AC solar Electrical services and does not accept any loss in Solar production due to national grid failure or outage.",
  "payment.section.4.p.3.solar":
    "The SEAI grant is an SEAI client relationship, Swyft Energy cannot accept any responsibilities for any grant schemes ending or being discontinued without notice from the Government body SEAI",

  "payment.section.5.header": "Recommendations ",
  "payment.section.5.p.1.boiler":
    "When a new boiler is fitted, sludge may move around the system preventing radiators from heating. We advise you include a flush with your installation to prevent this.",
  "payment.section.5.p.2.boiler":
    "It is advised customers install a new cylinder when replacing their boiler. Old cylinders may leak when fitting a new boiler.",

  "payment.section.6.p.1":
    "You can view all of the terms and conditions by selecting the link below. We ask that you take the time to review them and if you have any questions, please contact us.",
  "payment.section.6.button": "Terms & Conditions"
};

export const PaymentPage: React.FC = () => {
  eventAnalytic.useEffect(EEventType.PagePaymentView);
  const classes = useStyles();

  const { quote } = useSelector((state: RootState) => state.quote);

  const type = quote?.type ?? EQuoteType.Boiler;

  const section1 = (
    <Box py={3}>
      <Typography variant="h2" paragraph>
        {strings["payment.section.1.header"]}
      </Typography>
      <Typography variant="body1" paragraph>
        {strings["payment.section.1.p.1"]}
      </Typography>
    </Box>
  );

  const section2 =
    type === EQuoteType.Boiler ? (
      <Box py={3}>
        <Typography variant="h2" paragraph>
          {strings["payment.section.2.header"]}
        </Typography>
        <Typography variant="body1" paragraph>
          {strings["payment.section.2.p.1.boiler"]}
        </Typography>
        <Typography variant="body1" paragraph>
          {strings["payment.section.2.p.2.boiler"]}
        </Typography>
        <Typography variant="body1" paragraph>
          {strings["payment.section.2.p.3.boiler"]}
        </Typography>
      </Box>
    ) : (
      <Box py={3}>
        <Typography variant="h2" paragraph>
          {strings["payment.section.2.header"]}
        </Typography>
        <Typography variant="body1" paragraph>
          {strings["payment.section.2.p.1.solar"].replaceAll(DepositAmountToken, quoteDepositAmount(quote).toString())}
        </Typography>
        <Typography variant="body1" paragraph>
          {strings["payment.section.2.p.2.solar"]}
        </Typography>
        <Typography variant="body1" paragraph>
          {strings["payment.section.2.p.3.solar"]}
        </Typography>
      </Box>
    );

  const section3 = (
    <Box py={3}>
      <Typography variant="h2" paragraph>
        {strings["payment.section.3.header"]}
      </Typography>
      <Typography variant="body1" paragraph>
        {strings["payment.section.3.p.1"]}
      </Typography>
      <Table className={classes.table}>
        <TableBody>
          <TableRow>
            <TableCell className={classes.alignRight}>Account Name: </TableCell>
            <TableCell>Ardrar Ltd</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.alignRight}>IBAN: </TableCell>
            <TableCell>IE82AIBK93638371796009</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.alignRight}>BIC: </TableCell>
            <TableCell>AIBKIE2DXXX</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );

  const section4 =
    type === EQuoteType.Boiler ? (
      <Box py={3}>
        <Typography variant="h2" paragraph>
          {strings["payment.section.4.header"]}
        </Typography>
        <Typography variant="body1" paragraph>
          {strings["payment.section.4.p.1.boiler"]}
        </Typography>
      </Box>
    ) : (
      <Box py={3}>
        <Typography variant="h2" paragraph>
          {strings["payment.section.4.header"]}
        </Typography>
        <Typography variant="body1" paragraph>
          {strings["payment.section.4.p.1.solar"]}
        </Typography>
        <Typography variant="body1" paragraph>
          {strings["payment.section.4.p.2.solar"]}
        </Typography>
        <Typography variant="body1" paragraph>
          {strings["payment.section.4.p.3.solar"]}
        </Typography>
      </Box>
    );

  const section5 =
    type === EQuoteType.Boiler ? (
      <Box py={3}>
        <Typography variant="h2" paragraph>
          {strings["payment.section.5.header"]}
        </Typography>
        <Typography variant="body1" paragraph>
          {strings["payment.section.5.p.1.boiler"]}
        </Typography>
        <Typography variant="body1" paragraph>
          {strings["payment.section.5.p.2.boiler"]}
        </Typography>
      </Box>
    ) : (
      <Box py={3}></Box>
    );

  const section6 = (
    <Box textAlign="center">
      <Typography variant="body1" paragraph>
        {strings["payment.section.6.p.1"]}
      </Typography>
      <Button component={RouterLink} to="/terms" variant="contained" color="secondary">
        {strings["payment.section.6.button"]}
      </Button>
    </Box>
  );

  const sections = (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        {section1}
      </Grid>
      <Grid item xs={12} md={6}>
        {section2}
        {section3}
      </Grid>
      <Grid item xs={12} md={6}>
        {section4}
        {section5}
      </Grid>
      <Grid item xs={12}>
        {section6}
      </Grid>
    </Grid>
  );

  return (
    <React.Fragment>
      <Box className={classes.banner}>
        <Container maxWidth="lg">
          <Typography variant="h5">{strings["payment.header"]}</Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Box py={3}>{sections}</Box>
      </Container>

      <Box className={classes.alternate} mb={2} py={4} textAlign="center">
        <OverrideTheme>
          <ReviewContainer />
        </OverrideTheme>
      </Box>
    </React.Fragment>
  );
};
